import { useCallback, useEffect, useState } from 'react'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated, selectUser } from '@/features/auth/authSlice'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { AdvancedDateRangePicker, ANGULAR_COMMUNICATION_LOG_PRESETS } from '@/components/ui/AdvancedDateRangePicker'
import { useUserRole } from '@/hooks/useUserRole'
import { getCompanies, getOrganizations, getProperties } from '@/features/invitee/services/inviteeService'
import { getTeams } from '../services/teamsService'
import { CHARITY_ROLES, LIBERTY_ROLES } from '@/constants/roles'
import type { TeamRecord } from '../types/team'
import type { TransferFilterFormValues } from '../types/transferFilterForm'
import {
  TRANSFER_PROCESS_NAME_OPTIONS,
  TRANSFER_STATUS_FILTER_OPTIONS,
} from '../constants/transferFilterOptions'
import { Filter, X } from 'lucide-react'
import type { TransferListFiltersProps } from '../types'

/**
 * Slide-out filter drawer for the transfer list (org, company, property, transfer type, dates, status, team).
 * Search is controlled on the page toolbar, not here.
 */
export function TransferListFilters({
  form,
  onFiltersChange,
  onReset,
  isOpen,
  onClose,
}: TransferListFiltersProps) {
  const user = useAppSelector(selectUser)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const { roleName } = useUserRole()

  const [teams, setTeams] = useState<TeamRecord[]>([])
  const [organizations, setOrganizations] = useState<SearchableSelectOption[]>([])
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
  const [properties, setProperties] = useState<SearchableSelectOption[]>([])
  const [loadingOrgs, setLoadingOrgs] = useState(false)
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingProperties, setLoadingProperties] = useState(false)

  const isLibertyRole = (LIBERTY_ROLES as readonly string[]).includes(roleName)
  const isCharityRole = (CHARITY_ROLES as readonly string[]).includes(roleName)

  const lockedOrgId = user?.current_role?.org_id != null && !isLibertyRole ? String(user.current_role.org_id) : undefined
  const lockedCompanyId = user?.current_role?.company_id != null ? String(user.current_role.company_id) : undefined
  const lockedPropertyId = user?.current_role?.property_id != null ? String(user.current_role.property_id) : undefined
  const readOnlyOrg = Boolean(lockedOrgId)
  const readOnlyCompany = Boolean(lockedCompanyId)
  const readOnlyProperty = Boolean(lockedPropertyId)

  const loadOrganizations = useCallback(
    async (search: string) => {
      if (!isAuthenticated) return
      setLoadingOrgs(true)
      try {
        const data = await getOrganizations(search)
        setOrganizations(
          data.map((org) => ({ value: String(org.org_id), label: org.org_name || String(org.org_id) })),
        )
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingOrgs(false)
      }
    },
    [isAuthenticated],
  )

  const loadCompanies = useCallback(
    async (search: string, orgIdOverride?: string) => {
      if (!isAuthenticated) return
      setLoadingCompanies(true)
      try {
        const orgId = (orgIdOverride ?? form.org_id) || undefined
        const data = await getCompanies(search, orgId)
        setCompanies(
          data.map((c) => ({ value: String(c.company_id), label: c.company_name || String(c.company_id) })),
        )
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingCompanies(false)
      }
    },
    [isAuthenticated, form.org_id],
  )

  const loadProperties = useCallback(
    async (search: string, companyIdOverride?: string, orgIdOverride?: string) => {
      if (!isAuthenticated) return
      setLoadingProperties(true)
      try {
        const companyId = (companyIdOverride ?? form.company_id) || undefined
        const orgId = (orgIdOverride ?? form.org_id) || undefined
        const data = await getProperties(search, companyId, orgId)
        setProperties(
          data.map((p) => ({ value: String(p.property_id), label: p.property_name || String(p.property_id) })),
        )
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingProperties(false)
      }
    },
    [isAuthenticated, form.org_id, form.company_id],
  )

  useEffect(() => {
    if (!isAuthenticated) return
    getTeams()
      .then(setTeams)
      .catch(() => setTeams([]))
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) return
    if (isLibertyRole || isCharityRole) {
      if (!lockedOrgId && organizations.length === 0) void loadOrganizations('')
    }
  }, [isAuthenticated, isCharityRole, isLibertyRole, loadOrganizations, lockedOrgId, organizations.length])

  useEffect(() => {
    if (!isAuthenticated) return
    if (!lockedPropertyId && properties.length === 0) void loadProperties('')
    if (!lockedCompanyId && companies.length === 0) void loadCompanies('')
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps -- match InviteeFilters

  /** Re-apply role-locked org/company/property when they drift (parity with InviteeFilters). */
  useEffect(() => {
    if (!isAuthenticated) return
    const locked: Partial<TransferFilterFormValues> = {}
    if (readOnlyOrg && lockedOrgId && form.org_id !== lockedOrgId) locked.org_id = lockedOrgId
    if (readOnlyCompany && lockedCompanyId && form.company_id !== lockedCompanyId) locked.company_id = lockedCompanyId
    if (readOnlyProperty && lockedPropertyId && form.property_id !== lockedPropertyId) locked.property_id = lockedPropertyId
    if (Object.keys(locked).length > 0) onFiltersChange(locked)
  }, [
    isAuthenticated,
    readOnlyOrg,
    readOnlyCompany,
    readOnlyProperty,
    lockedOrgId,
    lockedCompanyId,
    lockedPropertyId,
    form.org_id,
    form.company_id,
    form.property_id,
    onFiltersChange,
  ])

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) window.addEventListener('keydown', onEsc)
    return () => window.removeEventListener('keydown', onEsc)
  }, [isOpen, onClose])

  const onOrgIdChange = (orgId: string) => {
    if (readOnlyOrg) return
    onFiltersChange({ org_id: orgId, company_id: '', property_id: '' })
    setCompanies([])
    setProperties([])
    void loadCompanies('', orgId)
  }

  const propertyDisabled =
    readOnlyProperty ||
    (!form.company_id && !lockedCompanyId && !form.org_id && !lockedOrgId)

  return (
    <>
      <div
        data-testid="transfer-list-filters-drawer-backdrop"
        className={`fixed inset-0 z-[2147483646] bg-black/20 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
        aria-hidden={!isOpen}
      />

      <div
        data-testid="transfer-list-filters-drawer"
        className={`fixed top-0 right-0 z-[2147483647] flex h-full w-full touch-scroll flex-col overflow-y-auto bg-white shadow-2xl transition-transform duration-300 ease-in-out sm:max-w-sm ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div
          data-testid="transfer-list-filters-drawer-header"
          className="flex items-center justify-between border-b border-slate-100 px-6 py-4"
        >
          <div className="flex items-center gap-2 text-slate-800">
            <Filter className="h-5 w-5 text-button" />
            <h2 className="text-lg font-bold" data-testid="transfer-list-filters-drawer-title">
              Filters
            </h2>
          </div>
          <button
            type="button"
            data-testid="transfer-list-filters-drawer-close-button"
            onClick={onClose}
            className="-mr-2 rounded-full p-2 text-slate-400 transition-all hover:bg-slate-50 hover:text-slate-600"
            aria-label="Close filters"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div
          data-testid="transfer-list-filters-drawer-content"
          className="flex-1 space-y-6 overflow-y-auto p-6"
        >
          <div className="space-y-2" data-testid="transfer-list-filters-transfer-type-section">
            <label
              className="text-xs font-semibold tracking-wider text-slate-500 uppercase"
              htmlFor="transfer-filters-process-name"
            >
              Transfer type
            </label>
            <div className="relative pill-filter-wrapper-vertical">
              <select
                id="transfer-filters-process-name"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pr-3 pl-4 text-sm"
                value={form.process_name}
                onChange={(e) => onFiltersChange({ process_name: e.target.value })}
              >
                {TRANSFER_PROCESS_NAME_OPTIONS.map((o) => (
                  <option key={o.label + o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2" data-testid="transfer-list-filters-organization-section">
            <label className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Organization</label>
            <div className="relative pill-filter-wrapper-vertical">
              <SearchableSelect
                triggerDataTestId="transfer-list-filters-organization-select-trigger"
                placeholder="Select Organization"
                options={readOnlyOrg
                  ? [{ value: lockedOrgId!, label: user?.current_role?.org_name || lockedOrgId! }]
                  : organizations}
                value={readOnlyOrg ? lockedOrgId! : (form.org_id || '')}
                onChange={(val) => {
                  if (readOnlyOrg) return
                  const orgId = String(Array.isArray(val) ? val[0] : val || '')
                  onOrgIdChange(orgId)
                }}
                onSearch={readOnlyOrg ? undefined : loadOrganizations}
                loading={loadingOrgs}
                disabled={readOnlyOrg}
              />
            </div>
          </div>

          <div className="space-y-2" data-testid="transfer-list-filters-company-section">
            <label className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Company</label>
            <div className="relative pill-filter-wrapper-vertical">
              <SearchableSelect
                triggerDataTestId="transfer-list-filters-company-select-trigger"
                placeholder="Select Company"
                options={readOnlyCompany
                  ? [{ value: lockedCompanyId!, label: user?.current_role?.company_name || lockedCompanyId! }]
                  : companies}
                value={readOnlyCompany ? lockedCompanyId! : (form.company_id || '')}
                onChange={(val) => {
                  if (readOnlyCompany) return
                  const id = String(Array.isArray(val) ? val[0] : val || '')
                  onFiltersChange({ company_id: id, property_id: '' })
                  setProperties([])
                  if (id && !readOnlyProperty) void loadProperties('', id, form.org_id || undefined)
                }}
                onSearch={readOnlyCompany ? undefined : (s) => void loadCompanies(s)}
                immediateSearchOnPaste
                loading={loadingCompanies}
                disabled={readOnlyCompany}
              />
            </div>
          </div>

          <div className="space-y-2" data-testid="transfer-list-filters-property-section">
            <label className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Property</label>
            <div className="relative pill-filter-wrapper-vertical">
              <SearchableSelect
                triggerDataTestId="transfer-list-filters-property-select-trigger"
                placeholder="Select Property"
                options={readOnlyProperty
                  ? [{ value: lockedPropertyId!, label: user?.current_role?.property_name || lockedPropertyId! }]
                  : properties}
                value={readOnlyProperty ? lockedPropertyId! : (form.property_id || '')}
                onChange={(val) => {
                  if (readOnlyProperty) return
                  const id = String(Array.isArray(val) ? val[0] : val || '')
                  onFiltersChange({ property_id: id })
                }}
                onSearch={readOnlyProperty ? undefined : (s) => void loadProperties(s)}
                immediateSearchOnPaste
                disabled={readOnlyProperty || propertyDisabled}
                loading={loadingProperties}
              />
            </div>
          </div>

          <div className="space-y-2" data-testid="transfer-list-filters-date-range-section">
            <label className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Date range</label>
            <div className="relative pill-filter-wrapper-vertical" data-testid="transfer-list-filters-date-range-wrap">
              <AdvancedDateRangePicker
                startDate={form.dateStart}
                endDate={form.dateEnd}
                onChange={(start, end) => onFiltersChange({ dateStart: start, dateEnd: end })}
                placeholder="Select Date Range"
                customPresets={ANGULAR_COMMUNICATION_LOG_PRESETS}
                presetsOnTop
                presetsSingleColumn
                monthsShown={2}
                stackMonthsVertically
                dropdownPortalToBody
                dropdownPortalCenterInFilterDrawer
                dropdownPortalZIndex={2147483647}
                dropdownPanelClassName="overflow-x-hidden"
                hideTriggerIcon
                triggerClassName="min-h-[48px] rounded-xl border-slate-200 bg-slate-50 shadow-none hover:border-slate-300 hover:bg-white focus-visible:ring-2 focus-visible:ring-blue-100"
              />
            </div>
          </div>

          <div className="space-y-2" data-testid="transfer-list-filters-status-section">
            <label className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Status</label>
            <div className="relative pill-filter-wrapper-vertical">
              <select
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pr-3 pl-4 text-sm"
                value={form.status}
                onChange={(e) => onFiltersChange({ status: e.target.value })}
              >
                {TRANSFER_STATUS_FILTER_OPTIONS.map((o) => (
                  <option key={o.label + o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {teams.length > 0 && (
            <div className="space-y-2" data-testid="transfer-list-filters-team-section">
              <label className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Team</label>
              <div className="relative pill-filter-wrapper-vertical">
                <select
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pr-3 pl-4 text-sm"
                  value={form.fk_team_id}
                  onChange={(e) => onFiltersChange({ fk_team_id: e.target.value })}
                >
                  <option value="">Select Team</option>
                  {teams.map((t) => (
                    <option key={String(t.ID)} value={String(t.ID)}>
                      {t.team_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div
          data-testid="transfer-list-filters-drawer-footer"
          className="shrink-0 border-t border-slate-100 bg-slate-50 p-4 sm:p-6 flex flex-wrap items-center justify-between gap-4"
        >
          <button
            type="button"
            data-testid="transfer-list-filters-reset-all-button"
            onClick={onReset}
            className="filter-drawer-reset-all"
          >
            Reset All
          </button>
          <button
            type="button"
            data-testid="transfer-list-filters-done-button"
            onClick={onClose}
            className="flex-1 rounded-lg bg-[rgb(21_151_255)] py-2.5 text-sm font-semibold text-white shadow-md shadow-[rgba(21,151,255,0.25)] transition hover:bg-[#0d7acc]"
          >
            Done
          </button>
        </div>

        <style>{`
            .pill-filter-wrapper-vertical .searchable-select-trigger {
                width: 100% !important;
                border-radius: 0.75rem !important;
                background-color: #f8fafc !important;
                border: 1px solid #e2e8f0 !important;
                min-height: 48px !important;
                padding-left: 1rem !important;
                padding-right: 0.75rem !important;
            }
            .pill-filter-wrapper-vertical .searchable-select-trigger:hover {
                background-color: #ffffff !important;
                border-color: #cbd5e1 !important;
            }
            .pill-filter-wrapper-vertical .custom-calendar-wrapper button {
                width: auto !important;
                min-height: auto !important;
                padding-left: 0 !important;
                background-color: transparent !important;
                border-color: transparent !important;
                border-radius: 9999px !important;
            }
            .pill-filter-wrapper-vertical .custom-calendar-wrapper button:hover {
                background-color: #f1f5f9 !important;
                border-color: transparent !important;
            }
        `}</style>
      </div>
    </>
  )
}
